import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keeps a modal mounted through its exit animation before calling onClose.
 * Returns { mounted, closing, requestClose }.
 */
export function useAnimatedModal(open, onClose, durationMs = 280) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setClosing(false);
      setMounted(true);
      return undefined;
    }
    if (!mounted) return undefined;
    setClosing(true);
    timerRef.current = setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, durationMs);
    return () => clearTimeout(timerRef.current);
  }, [open, mounted, durationMs]);

  const requestClose = useCallback(() => {
    if (closing) return;
    clearTimeout(timerRef.current);
    setClosing(true);
    timerRef.current = setTimeout(() => {
      setMounted(false);
      setClosing(false);
      onClose?.();
    }, durationMs);
  }, [closing, onClose, durationMs]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { mounted, closing, requestClose };
}

export default useAnimatedModal;
